import { apiRequest } from "./apiClient";

export type FeedbackCategory = 'bug' | 'feature' | 'improvement' | 'general' | 'complaint';
export type FeedbackStatus = 'pending' | 'reviewed' | 'resolved' | 'archived';
export type FeedbackPriority = 'low' | 'medium' | 'high' | 'critical';

export type Feedback = {
  _id: string;
  userId: string;
  userEmail: string;
  userName: string;
  subject: string;
  message: string;
  category: FeedbackCategory;
  priority: FeedbackPriority;
  status: FeedbackStatus;
  adminResponse: string;
  respondedBy: string | null;
  respondedAt: string | null;
  rating: number | null;
  tags: string[];
  pageUrl: string;
  createdAt: string;
  updatedAt: string;
};

export type FeedbackStats = {
  total: number;
  pending: number;
  reviewed: number;
  resolved: number;
  archived: number;
  byCategory: Record<string, number>;
  byPriority: Record<string, number>;
  averageRating: number | null;
  thisWeek: number;
  thisMonth: number;
};

export type PaginatedFeedback = {
  data: Feedback[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
};

export type CreateFeedbackDto = {
  subject?: string;
  message?: string;
  category?: FeedbackCategory;
  rating?: number;
  pageUrl?: string;
  tags?: string[];
};

export type UpdateFeedbackDto = {
  status?: FeedbackStatus;
  priority?: FeedbackPriority;
  adminResponse?: string;
  tags?: string[];
};

export type FeedbackQueryParams = {
  status?: FeedbackStatus;
  category?: FeedbackCategory;
  priority?: FeedbackPriority;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'priority' | 'status' | 'category';
  sortOrder?: 'asc' | 'desc';
};

const buildQueryString = (params: FeedbackQueryParams): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

// User endpoints
export const createFeedback = (dto: CreateFeedbackDto) =>
  apiRequest<Feedback>("/feedback", {
    method: "POST",
    body: JSON.stringify(dto),
  });

export const getMyFeedback = (params: FeedbackQueryParams = {}) =>
  apiRequest<PaginatedFeedback>(`/feedback/my${buildQueryString(params)}`);

export const getMyFeedbackById = (id: string) =>
  apiRequest<Feedback>(`/feedback/my/${id}`);

// Admin endpoints
export const getAllFeedback = (params: FeedbackQueryParams = {}) =>
  apiRequest<PaginatedFeedback>(`/feedback${buildQueryString(params)}`);

export const getFeedbackStats = () =>
  apiRequest<FeedbackStats>("/feedback/stats");

export const getFeedbackById = (id: string) =>
  apiRequest<Feedback>(`/feedback/${id}`);

export const updateFeedback = (id: string, dto: UpdateFeedbackDto) =>
  apiRequest<Feedback>(`/feedback/${id}`, {
    method: "PATCH",
    body: JSON.stringify(dto),
  });

export const deleteFeedback = (id: string) =>
  apiRequest<{ deleted: boolean }>(`/feedback/${id}`, {
    method: "DELETE",
  });

export const bulkUpdateFeedbackStatus = (ids: string[], status: FeedbackStatus) =>
  apiRequest<{ updated: number }>("/feedback/bulk/status", {
    method: "PATCH",
    body: JSON.stringify({ ids, status }),
  });
